// frontend/src/pages/Customer/Customer.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Building,
  CreditCard,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Save,
  User,
  UserPlus,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./Customers.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ ROBUST: ALWAYS returns a 3‑letter currency code as a string
const getCompanyCurrency = (company) => {
  if (!company) return "USD";

  let currency = null;

  // 1. Try company.currency (could be object or string)
  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  // 2. Try company.currency_code
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  // 3. Try company.base_currency
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  // Final safety: default to USD if not a valid 3-letter code
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const initialForm = (companyId = "", companyCurrency = "USD") => ({
  customer_name: "",
  customer_type: "regular",
  phone: "",
  email: "",
  address: "",
  currency_code: companyCurrency,
  credit_limit: "0.00",
  company_id: companyId,
});

export default function Customer() {
  const { currentUser, userType, company } = useAuth();
  const { getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const companyId = company?._id || company || currentUser?.company_id || "";
  const isSystemAdmin =
    userType === "system" && ["developer", "admin"].includes(currentUser?.role);
  const isCompanyUser =
    userType === "company" &&
    ["admin", "sales", "user"].includes(currentUser?.role);
  const canCreate = isSystemAdmin || isCompanyUser;

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [formData, setFormData] = useState(() =>
    initialForm(isSystemAdmin ? "" : companyId, companyCurrency)
  );
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const selectedCompanyName = useMemo(() => {
    if (!isSystemAdmin) return company?.company_name || "your company";
    return (
      companies.find((item) => item._id === formData.company_id)?.company_name ||
      "the selected company"
    );
  }, [companies, company?.company_name, formData.company_id, isSystemAdmin]);

  // When system admin changes company, update currency
  useEffect(() => {
    if (isSystemAdmin && formData.company_id) {
      const selectedCompany = companies.find((c) => c._id === formData.company_id);
      if (selectedCompany) {
        const currency = getCompanyCurrency(selectedCompany);
        setFormData((prev) => ({
          ...prev,
          currency_code: currency,
        }));
      }
    }
  }, [formData.company_id, companies, isSystemAdmin]);

  useEffect(() => {
    let active = true;

    const setup = async () => {
      if (!canCreate) {
        toast.error("You do not have permission to create customers");
        navigate("/");
        return;
      }

      if (isSystemAdmin) {
        try {
          const response = await axios.get(`${url}/api/company`, {
            headers: getAuthHeaders(),
          });
          if (active) setCompanies(response.data?.data || []);
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to load companies");
        }
      } else if (active) {
        setFormData((current) => ({
          ...current,
          company_id: companyId,
          currency_code: companyCurrency,
        }));
      }

      if (active) setLoading(false);
    };

    setup();
    return () => {
      active = false;
    };
  }, [canCreate, companyId, companyCurrency, isSystemAdmin, navigate]);

  const setField = (name, value) => {
    setFormData((current) => {
      const next = { ...current, [name]: value };
      if (name === "customer_type" && value === "walk_in") {
        next.credit_limit = "0.00";
      }
      return next;
    });
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const name = formData.customer_name.trim();
    const creditLimit = Number(formData.credit_limit || 0);

    if (name.length < 2 || name.length > 100) {
      nextErrors.customer_name = "Customer name must contain 2 to 100 characters";
    }
    if (formData.phone && !/^[\d\s+()\-]{7,30}$/.test(formData.phone.trim())) {
      nextErrors.phone = "Enter a valid phone number";
    }
    if (
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      nextErrors.email = "Enter a valid email address";
    }
    if (!/^[A-Za-z]{3}$/.test(formData.currency_code)) {
      nextErrors.currency_code = "Use a three-letter currency code, such as USD";
    }
    if (!Number.isFinite(creditLimit) || creditLimit < 0) {
      nextErrors.credit_limit = "Credit limit must be zero or greater";
    }
    if (!formData.company_id) {
      nextErrors.company_id = "Company selection is required";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields");
      return;
    }

    const payload = {
      customer_name: formData.customer_name.trim(),
      customer_type: formData.customer_type,
      phone: formData.phone.trim(),
      email: formData.email.trim().toLowerCase(),
      address: formData.address.trim(),
      currency_code: formData.currency_code.trim().toUpperCase(),
      credit_limit:
        formData.customer_type === "walk_in"
          ? 0
          : Number(formData.credit_limit || 0),
      company_id: formData.company_id,
    };

    try {
      setSubmitting(true);
      const response = await axios.post(`${url}/api/customer`, payload, {
        headers: getAuthHeaders(),
      });

      toast.success(response.data?.message || "Customer created successfully");
      setFormData(initialForm(isSystemAdmin ? "" : companyId, companyCurrency));
      window.setTimeout(() => navigate("/ViewCustomer"), 500);
    } catch (error) {
      const response = error.response?.data;
      const message = response?.message || "Failed to create customer";
      toast.error(message);

      if (response?.code === "INACTIVE_CUSTOMER_EXISTS") {
        setErrors((current) => ({ ...current, customer_name: message }));
      }
      if (Array.isArray(response?.errors)) {
        setErrors((current) => ({
          ...current,
          ...Object.fromEntries(response.errors.map((item) => [item.field, item.message])),
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="customer-page-shell">
        <div className="customer-loading-card">
          <Loader2 className="customer-spin" size={30} />
          <span>Loading customer form…</span>
        </div>
      </main>
    );
  }

  return (
    <main className="customer-page-shell">
      <section className="customer-form-card">
        <button
          type="button"
          className="customer-close-button"
          onClick={() => navigate("/ViewCustomer")}
          aria-label="Close customer form"
        >
          <X size={20} />
        </button>

        <header className="customer-form-header">
          <div className="customer-form-icon"><UserPlus size={24} /></div>
          <div>
            <p className="customer-eyebrow">Customer master</p>
            <h1>Add customer</h1>
            <p>Create a customer record for {selectedCompanyName}.</p>
          </div>
        </header>

        <div className="customer-accounting-note">
          <AlertCircle size={20} />
          <div>
            <strong>Customer creation does not post accounting entries.</strong>
            <span>
              Opening balances must be entered through the Beginning Balance Entry screen.
              This form only creates master data.
            </span>
          </div>
        </div>

        <form className="customer-form" onSubmit={handleSubmit} noValidate>
          {isSystemAdmin && (
            <div className="customer-field customer-field--full">
              <label htmlFor="customer-company"><Building size={15} /> Company *</label>
              <select
                id="customer-company"
                value={formData.company_id}
                onChange={(event) => setField("company_id", event.target.value)}
              >
                <option value="">Select company</option>
                {companies.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.company_name}{item.company_code ? ` (${item.company_code})` : ""}
                  </option>
                ))}
              </select>
              {errors.company_id && <small className="customer-error">{errors.company_id}</small>}
            </div>
          )}

          <div className="customer-field customer-field--full">
            <label htmlFor="customer-name"><User size={15} /> Customer name *</label>
            <input
              id="customer-name"
              value={formData.customer_name}
              onChange={(event) => setField("customer_name", event.target.value)}
              placeholder="Example: Mohamed Retail Customer"
              maxLength={100}
            />
            {errors.customer_name && <small className="customer-error">{errors.customer_name}</small>}
          </div>

          <div className="customer-field">
            <label htmlFor="customer-type"><User size={15} /> Customer type *</label>
            <select
              id="customer-type"
              value={formData.customer_type}
              onChange={(event) => setField("customer_type", event.target.value)}
            >
              <option value="walk_in">Walk In</option>
              <option value="regular">Regular</option>
              <option value="credit">Credit</option>
            </select>
            {errors.customer_type && <small className="customer-error">{errors.customer_type}</small>}
          </div>

          <div className="customer-field">
            <label htmlFor="customer-phone"><Phone size={15} /> Phone</label>
            <input
              id="customer-phone"
              value={formData.phone}
              onChange={(event) => setField("phone", event.target.value)}
              placeholder="Optional"
              maxLength={30}
            />
            {errors.phone && <small className="customer-error">{errors.phone}</small>}
          </div>

          <div className="customer-field">
            <label htmlFor="customer-email"><Mail size={15} /> Email</label>
            <input
              id="customer-email"
              type="email"
              value={formData.email}
              onChange={(event) => setField("email", event.target.value)}
              placeholder="Optional — duplicate emails are allowed"
              maxLength={120}
            />
            {errors.email && <small className="customer-error">{errors.email}</small>}
          </div>

          {/* ✅ Currency field – disabled, always shows a clean string */}
          <div className="customer-field">
            <label htmlFor="customer-currency"><CreditCard size={15} /> Currency</label>
            <input
              id="customer-currency"
              value={formData.currency_code}
              disabled
              style={{
                background: '#f5f5f5',
                cursor: 'not-allowed',
                opacity: 0.8,
              }}
            />
            <small style={{ color: '#666' }}>
              Currency is set to the company's base currency and cannot be changed.
            </small>
            {errors.currency_code && <small className="customer-error">{errors.currency_code}</small>}
          </div>

          <div className="customer-field customer-field--full">
            <label htmlFor="customer-address"><MapPin size={15} /> Address</label>
            <input
              id="customer-address"
              value={formData.address}
              onChange={(event) => setField("address", event.target.value)}
              placeholder="Optional address"
              maxLength={250}
            />
          </div>

          <div className="customer-field customer-field--full">
            <label htmlFor="customer-credit-limit"><CreditCard size={15} /> Credit limit</label>
            <input
              id="customer-credit-limit"
              type="number"
              min="0"
              step="0.01"
              value={formData.credit_limit}
              onChange={(event) => setField("credit_limit", event.target.value)}
              disabled={formData.customer_type === "walk_in"}
            />
            <small className="customer-hint">
              Operational control only. Zero means no limit is configured; it does not post accounting.
            </small>
            {errors.credit_limit && <small className="customer-error">{errors.credit_limit}</small>}
          </div>

          <div className="customer-form-actions customer-field--full">
            <button
              type="button"
              className="customer-button customer-button--secondary"
              onClick={() => navigate("/ViewCustomer")}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="customer-button customer-button--primary"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="customer-spin" size={17} /> : <Save size={17} />}
              {submitting ? "Creating…" : "Create customer"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}